// src/video/DailyCallProvider.tsx
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import DailyIframe, { DailyCall } from '@daily-co/daily-js';
import { DailyProvider } from '@daily-co/daily-react';
import { getSupabaseClient } from '../lib/supabase';

// Singleton to ensure only one Daily call object exists
let globalCallObject: DailyCall | null = null;

type Ctx = {
  callObject: DailyCall | null;
  roomUrl: string | null;
  token: string | null;
  join: () => Promise<void>;
  leave: () => Promise<void>;
  end: () => Promise<void>;
};

const DailyCtx = createContext<Ctx>({
  callObject: null,
  roomUrl: null,
  token: null,
  join: async () => {},
  leave: async () => {},
  end: async () => {},
});

export function useDailyCall() {
  return useContext(DailyCtx);
}

export function DailyCallProvider({
  sessionId,
  children,
}: {
  sessionId: string;
  children: React.ReactNode;
}) {
  const [callObject, setCallObject] = useState<DailyCall | null>(null);
  const [roomUrl, setRoomUrl] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const cleanupScheduledRef = useRef(false);

  // Fetch room + token through the Supabase client so auth is handled for us.
  useEffect(() => {
    if (!sessionId) return;

    let mounted = true;
    const supabase = getSupabaseClient();

    (async () => {
      try {
        const res = await supabase.functions.invoke('video-token', {
          body: { session_id: sessionId }
        });
        if (res.error) throw res.error;
        const data = res.data as { roomUrl: string; token: string } | null;
        if (!data) throw new Error('No data returned from video-token function');
        if (!mounted) return;
        setRoomUrl(data.roomUrl);
        setToken(data.token);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [sessionId]);

  // Create call object once using singleton pattern
  useEffect(() => {
    // Reuse existing global call object
    if (globalCallObject) {
      setCallObject(globalCallObject);
      return;
    }

    // Create call object only if we don't have one globally
    try {
      const co = DailyIframe.createCallObject();
      globalCallObject = co;
      setCallObject(co);
    } catch (err) {
      console.error('[DailyProvider] Failed to create call object:', err);
    }

    return () => {
      // Schedule cleanup but don't execute immediately (for StrictMode)
      if (!cleanupScheduledRef.current) {
        cleanupScheduledRef.current = true;

        // Delay cleanup to allow StrictMode re-mount
        setTimeout(() => {
          // Only cleanup if component didn't remount
          if (cleanupScheduledRef.current && globalCallObject) {
            const co = globalCallObject;
            globalCallObject = null;
            setCallObject(null);

            // Async cleanup
            const cleanup = async () => {
              try {
                const state = co.meetingState();
                if (state === 'joined-meeting' || state === 'joining-meeting') {
                  await co.leave();
                }
              } catch (err) {
                // Ignore
              }

              try {
                if (typeof (co as any)?.stopCamera === 'function') {
                  await (co as any).stopCamera();
                }
              } catch (err) {
                // Ignore
              }

              try {
                co.destroy();
              } catch (err) {
                // Ignore
              }
            };

            cleanup().catch(() => {});
          }
          cleanupScheduledRef.current = false;
        }, 100);
      }
    };
  }, []);

  // Reset cleanup schedule on mount
  useEffect(() => {
    cleanupScheduledRef.current = false;
  }, []);

  // Pre-auth once we have credentials (optional but faster for join)
  useEffect(() => {
    if (!callObject || !roomUrl || !token) return;
    (async () => {
      try {
        await callObject.preAuth({ url: roomUrl, token });
      } catch (e) {
        console.error('[DailyProvider] preAuth failed', e);
      }
    })();
  }, [callObject, roomUrl, token]);

  const join = useCallback(async () => {
    if (!callObject || !roomUrl || !token) {
      console.warn('[DailyProvider] Cannot join - missing requirements:', {
        callObject: !!callObject,
        roomUrl: !!roomUrl,
        token: !!token
      });
      return;
    }

    console.log('[DailyProvider] Starting join process...');

    const audioDeviceId = localStorage.getItem('gc.audioDeviceId') || undefined;
    const videoDeviceId = localStorage.getItem('gc.videoDeviceId') || undefined;
    const audioPreference = localStorage.getItem('gc.microphoneEnabled');
    const videoPreference = localStorage.getItem('gc.cameraEnabled');
    const allowAudio = audioPreference !== 'false';
    const allowVideo = videoPreference !== 'false';

    console.log('[DailyProvider] User preferences:', { allowAudio, allowVideo });

    try {
      // Set input devices before starting camera
      if (audioDeviceId || videoDeviceId) {
        console.log('[DailyProvider] Setting input devices...');
        await callObject.setInputDevicesAsync({
          audioDeviceId,
          videoDeviceId,
        });
      }

      // GCS-119: Always acquire device tracks so they can be toggled on later.
      // Then mute before joining so the user's pre-join preference is respected.
      console.log('[DailyProvider] Starting camera (acquiring devices)...');
      await callObject.startCamera({
        audioSource: audioDeviceId || true,
        videoSource: videoDeviceId || true,
      });

      // Mute tracks BEFORE join so Daily doesn't publish them
      if (!allowAudio) callObject.setLocalAudio(false);
      if (!allowVideo) callObject.setLocalVideo(false);

      console.log('[DailyProvider] Joining room...');
      await callObject.join({
        url: roomUrl,
        token,
        startVideoOff: !allowVideo,
        startAudioOff: !allowAudio,
      });

      console.log('[DailyProvider] Successfully joined room');
    } catch (err) {
      console.error('[DailyProvider] Join failed:', err);
      throw err;
    }
  }, [callObject, roomUrl, token]);

  const leave = useCallback(async () => {
    if (!callObject) return;
    await callObject.leave();
  }, [callObject]);

  const end = useCallback(async () => {
    if (!callObject) return;
    try {
      await callObject.leave();
    } catch (err) {
      console.warn('[DailyProvider] leave warning', err);
    }
    const isDestroyed =
      typeof (callObject as { isDestroyed?: () => boolean }).isDestroyed === 'function' &&
      (callObject as { isDestroyed: () => boolean }).isDestroyed();
    if (!isDestroyed && typeof (callObject as { stopCamera?: () => Promise<void> }).stopCamera === 'function') {
      try {
        await (callObject as { stopCamera: () => Promise<void> }).stopCamera();
      } catch (err) {
        console.warn('[DailyProvider] stopCamera warning', err);
      }
    } else if (!isDestroyed) {
      try {
        callObject.setLocalVideo(false);
        callObject.setLocalAudio(false);
      } catch (err) {
        console.warn('[DailyProvider] setLocalTracks warning', err);
      }
    }
    try {
      callObject.destroy();
    } catch (err) {
      console.warn('[DailyProvider] destroy warning', err);
    }
    globalCallObject = null;
    setCallObject(null);
    setRoomUrl(null);
    setToken(null);
  }, [callObject]);

  const value = useMemo(
    () => ({ callObject, roomUrl, token, join, leave, end }),
    [callObject, roomUrl, token, join, leave, end]
  );

  return (
    <DailyCtx.Provider value={value}>
      <DailyProvider callObject={callObject}>{children}</DailyProvider>
    </DailyCtx.Provider>
  );
}
